import type { ManualReviewReadItem, OperatorIdentity } from "@billiard/contracts";
import { useEffect, useId, useRef, useState } from "react";

export type ManualReviewResolutionCategory =
  | "PAYMENT_RECONCILIATION"
  | "ORDER_SYNC_REVIEW"
  | "COACH_SERVICE_REVIEW";

export type ManualReviewResolutionItem = Extract<
  ManualReviewReadItem,
  { category: ManualReviewResolutionCategory }
> & { workItemId: string };

export type ManualReviewResolutionOutcome =
  | "MANUALLY_RECONCILED"
  | "NOT_RECEIVED"
  | "AMOUNT_MISMATCH"
  | "DUPLICATE_PAYMENT"
  | "KEEP_ISOLATED"
  | "TRANSFER_TO_FINANCE"
  | "TRANSFER_TO_OPERATIONS";

export type ManualReviewResolutionRequest = {
  category: ManualReviewResolutionCategory;
  resolutionCode: ManualReviewResolutionOutcome;
  note: string;
};

export type ManualReviewResolutionPreview = ManualReviewResolutionRequest & {
  kind: "MANUAL_REVIEW_RESOLUTION";
  workItemId: string;
  title: string;
  currentReason: string;
  amountFen: number | null;
  originalDocumentLabel: string;
  consequences: readonly string[];
  nextSteps: readonly string[];
  sourceReferenceSha256: string;
  businessFactSha256: string;
  commandSha256: string;
  canSubmit: boolean;
  blocker: string | null;
  generatedAt: string;
};

export type ManualReviewResolutionPanelProps = {
  item: ManualReviewResolutionItem;
  operator: Pick<OperatorIdentity, "id" | "displayName">;
  effectiveRoles: readonly OperatorIdentity["role"][];
  disabled?: boolean;
  onPreview: (
    workItemId: string,
    request: ManualReviewResolutionRequest,
  ) => Promise<ManualReviewResolutionPreview>;
  onSubmitApproval: (preview: ManualReviewResolutionPreview) => Promise<void>;
};

type ResolutionOption = {
  value: ManualReviewResolutionOutcome;
  label: string;
  explanation: string;
  consequence: string;
};

const paymentOptions: readonly ResolutionOption[] = [
  {
    value: "MANUALLY_RECONCILED",
    label: "人工确认已到账",
    explanation: "已在银行、微信或支付宝商户记录中找到与本单一致的真实到账。",
    consequence: "审批并安全执行成功后，系统才会把这笔付款记为已人工核对；当前不会提前入账。",
  },
  {
    value: "NOT_RECEIVED",
    label: "确认未到账",
    explanation: "顾客凭证无法对应真实到账，或收款账户明确没有收到这笔钱。",
    consequence: "这笔付款继续按未到账处理，不会把订单误记为已收款。",
  },
  {
    value: "AMOUNT_MISMATCH",
    label: "金额不一致",
    explanation: "查到的真实到账金额与本单登记金额不同，需要继续核对差额。",
    consequence: "系统保留金额差异并等待后续处理，不会用错误金额完成收款。",
  },
  {
    value: "DUPLICATE_PAYMENT",
    label: "重复付款凭证",
    explanation: "这张凭证已经对应另一笔付款，不能再次用于本单。",
    consequence: "系统保留重复凭证标记，不会重复增加营业收入或重复结清订单。",
  },
];

const orderOptions: readonly ResolutionOption[] = [
  {
    value: "KEEP_ISOLATED",
    label: "继续隔离",
    explanation: "现场证据不足，暂时不把这条冲突记录合入权威订单。",
    consequence: "原订单和报表保持不变；冲突记录继续留在待复核区，不会被悄悄丢弃。",
  },
  {
    value: "TRANSFER_TO_FINANCE",
    label: "转交财务复核",
    explanation: "冲突涉及收款、应收或金额口径，需要由财务责任人继续核对。",
    consequence: "事项转入财务复核队列，当前不会修改订单金额、付款或库存。",
  },
];

const coachOptions: readonly ResolutionOption[] = [
  {
    value: "KEEP_ISOLATED",
    label: "继续隔离",
    explanation: "上下钟或人员依据仍不完整，暂时不写入权威助教服务记录。",
    consequence: "原服务时长、服务费和提成保持不变；冲突记录继续等待核对。",
  },
  {
    value: "TRANSFER_TO_OPERATIONS",
    label: "转交营运复核",
    explanation: "需要由门店营运责任人核对球台、助教和上下钟现场记录。",
    consequence: "事项转入营运复核队列，当前不会自动上下钟，也不会新增、撤销或重算提成。",
  },
];

function optionsFor(category: ManualReviewResolutionCategory): readonly ResolutionOption[] {
  if (category === "PAYMENT_RECONCILIATION") return paymentOptions;
  return category === "ORDER_SYNC_REVIEW" ? orderOptions : coachOptions;
}

function categoryLabel(category: ManualReviewResolutionCategory): string {
  if (category === "PAYMENT_RECONCILIATION") return "付款待核对";
  return category === "ORDER_SYNC_REVIEW" ? "订单同步待核对" : "助教服务待核对";
}

function emptyOutcomeMessage(category: ManualReviewResolutionCategory): string {
  if (category === "PAYMENT_RECONCILIATION") return "请选择这笔付款的处理方式";
  return category === "ORDER_SYNC_REVIEW"
    ? "请选择这笔订单同步冲突的处理方式"
    : "请选择这笔助教服务冲突的处理方式";
}

function amountLabel(item: ManualReviewResolutionItem): string {
  if (item.category === "COACH_SERVICE_REVIEW" || item.amountFen === null) return "需要核对";
  return new Intl.NumberFormat("zh-CN", {
    style: "currency",
    currency: "CNY",
  }).format(item.amountFen / 100);
}

function feedbackMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message.trim().length > 0 ? error.message : fallback;
}

function previewMatchesRequest(
  preview: ManualReviewResolutionPreview,
  workItemId: string,
  request: ManualReviewResolutionRequest,
): boolean {
  return (
    preview.kind === "MANUAL_REVIEW_RESOLUTION" &&
    preview.workItemId === workItemId &&
    preview.category === request.category &&
    preview.resolutionCode === request.resolutionCode &&
    preview.note === request.note &&
    preview.title.trim().length > 0 &&
    preview.currentReason.trim().length > 0 &&
    preview.originalDocumentLabel.trim().length > 0 &&
    preview.consequences.length > 0 &&
    preview.consequences.every((value) => value.trim().length > 0) &&
    preview.nextSteps.length > 0 &&
    preview.nextSteps.every((value) => value.trim().length > 0) &&
    /^[0-9a-f]{64}$/u.test(preview.sourceReferenceSha256) &&
    /^[0-9a-f]{64}$/u.test(preview.businessFactSha256) &&
    /^[0-9a-f]{64}$/u.test(preview.commandSha256) &&
    typeof preview.canSubmit === "boolean" &&
    (preview.blocker === null || preview.blocker.trim().length > 0)
  );
}

export function ManualReviewResolutionPanel({
  item,
  operator,
  effectiveRoles,
  disabled = false,
  onPreview,
  onSubmitApproval,
}: ManualReviewResolutionPanelProps): React.JSX.Element {
  const id = useId();
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const [outcome, setOutcome] = useState<ManualReviewResolutionOutcome | "">("");
  const [note, setNote] = useState("");
  const [preview, setPreview] = useState<ManualReviewResolutionPreview | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [previewInvalidated, setPreviewInvalidated] = useState(false);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const activeItemKey = item.itemKey;
  const options = optionsFor(item.category);
  const canSubmit = effectiveRoles.some((role) => role === "OWNER" || role === "MANAGER");
  const writeLocked = disabled || previewing || submitting || submitted;
  const normalizedNote = note.trim();
  const outcomeValid = options.some((option) => option.value === outcome);
  const noteValid = normalizedNote.length >= 2;

  useEffect(() => {
    if (activeItemKey.length === 0) return;
    setOutcome("");
    setNote("");
    setPreview(null);
    setFeedback(null);
    setPreviewInvalidated(false);
    setValidationAttempted(false);
    setPreviewing(false);
    setSubmitting(false);
    setSubmitted(false);
  }, [activeItemKey]);

  const invalidatePreview = () => {
    if (preview) setPreviewInvalidated(true);
    setPreview(null);
    setFeedback(null);
    setSubmitted(false);
  };

  const requestPreview = async () => {
    if (!canSubmit || writeLocked) return;
    setValidationAttempted(true);
    setFeedback(null);
    if (!outcomeValid) {
      setFeedback(emptyOutcomeMessage(item.category));
      return;
    }
    if (!noteValid) {
      setFeedback("请填写至少 2 个字的核对说明，说清你核对了什么依据");
      noteRef.current?.focus();
      return;
    }
    const request: ManualReviewResolutionRequest = {
      category: item.category,
      resolutionCode: outcome as ManualReviewResolutionOutcome,
      note: normalizedNote,
    };
    setPreviewing(true);
    setPreviewInvalidated(false);
    try {
      const result = await onPreview(item.workItemId, request);
      if (!previewMatchesRequest(result, item.workItemId, request)) {
        setFeedback("预览与当前事项不一致，系统没有开放提交；请重新读取这笔待核对事项");
        return;
      }
      setPreview(result);
    } catch (error) {
      setFeedback(feedbackMessage(error, "暂时无法生成申请预览，本次没有提交"));
    } finally {
      setPreviewing(false);
    }
  };

  const submitApproval = async () => {
    if (!canSubmit || writeLocked || !preview || !preview.canSubmit) return;
    setFeedback(null);
    setSubmitting(true);
    try {
      await onSubmitApproval(preview);
      setSubmitted(true);
    } catch (error) {
      setFeedback(
        feedbackMessage(
          error,
          "审批申请结果暂时无法确认，请勿重复提交；请回统一审批中心查询原申请",
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="refund-approval-form" aria-label={`${categoryLabel(item.category)}处置`}>
      <header>
        <div>
          <span className="approval-kicker">{categoryLabel(item.category)}</span>
          <h3>{item.title}</h3>
          <p>{item.summary}</p>
        </div>
        <strong>{amountLabel(item)}</strong>
      </header>

      <dl className="approval-card-facts">
        <div>
          <dt>球台</dt>
          <dd>{item.tableNumber ?? "未关联球台"}</dd>
        </div>
        <div>
          <dt>营业日</dt>
          <dd>{item.businessDate ?? "需要核对"}</dd>
        </div>
        <div>
          <dt>当前建议</dt>
          <dd>{item.nextStep}</dd>
        </div>
        <div>
          <dt>申请人</dt>
          <dd>当前申请人：{operator.displayName}</dd>
        </div>
      </dl>

      <div className="approval-info">
        <div>
          <strong>先预览，再提交给另一人审批</strong>
          <p>
            本组件只提交审批申请，不在本页直接改业务。当前不会修改付款、订单、助教服务或提成；申请人与批准人必须不同，最终由服务端再次核验。
          </p>
        </div>
      </div>

      <fieldset className="approval-consequence-grid" disabled={!canSubmit || writeLocked}>
        <legend>选择处理方式</legend>
        {options.map((option) => (
          <section key={option.value}>
            <label>
              <input
                type="radio"
                name={`manual-review-resolution-${id}`}
                value={option.value}
                checked={outcome === option.value}
                disabled={!canSubmit || writeLocked}
                onChange={() => {
                  invalidatePreview();
                  setOutcome(option.value);
                }}
              />
              <strong>{option.label}</strong>
            </label>
            <p>{option.explanation}</p>
            <p>
              <strong>选择后会发生什么：</strong>
              {option.consequence}
            </p>
          </section>
        ))}
      </fieldset>

      {canSubmit ? (
        <>
          <label htmlFor={`manual-review-note-${id}`}>
            核对说明
            <small>至少填写 2 个字。请写明查看了哪张凭证、原单或上下钟记录。</small>
            <textarea
              ref={noteRef}
              id={`manual-review-note-${id}`}
              name={`manual-review-note-${item.itemKey}`}
              value={note}
              maxLength={500}
              rows={4}
              disabled={writeLocked}
              aria-invalid={validationAttempted && !noteValid ? "true" : undefined}
              onChange={(event) => {
                invalidatePreview();
                setNote(event.currentTarget.value);
              }}
              placeholder="例如：已核对当日商户到账记录，金额和付款时间一致"
            />
          </label>

          {previewInvalidated ? (
            <output className="approval-warning">
              选择或说明已变化，旧预览已失效；请重新检查经营后果。
            </output>
          ) : null}
          {feedback ? (
            <div className="approval-warning" role="alert">
              {feedback}
            </div>
          ) : null}

          {preview ? (
            <section className="refund-approval-preview" aria-label="人工复核处置申请预览">
              <header>
                <div>
                  <span>已生成申请预览</span>
                  <strong>{preview.title}</strong>
                </div>
                <span>{preview.canSubmit ? "可以提交审批" : "暂时不能提交"}</span>
              </header>
              <div className="approval-consequence-grid">
                <section>
                  <h4>批准并安全执行后</h4>
                  <ul>
                    {preview.consequences.map((value) => (
                      <li key={value}>{value}</li>
                    ))}
                  </ul>
                </section>
                <section>
                  <h4>提交后的步骤</h4>
                  <ol>
                    {preview.nextSteps.map((value) => (
                      <li key={value}>{value}</li>
                    ))}
                  </ol>
                </section>
              </div>
              {preview.blocker ? (
                <div className="approval-warning" role="alert">
                  {preview.blocker}
                </div>
              ) : null}
            </section>
          ) : null}

          {submitted ? (
            <output className="success-band">
              审批申请已提交。当前业务仍未改变，请由另一位老板或店长在统一审批中心处理。
            </output>
          ) : (
            <div className="approval-actions">
              <button
                className="button secondary"
                type="button"
                disabled={writeLocked}
                onClick={() => void requestPreview()}
              >
                {previewing ? "正在检查" : "检查选择和经营后果"}
              </button>
              {preview?.canSubmit ? (
                <button
                  className="button primary"
                  type="button"
                  disabled={writeLocked}
                  onClick={() => void submitApproval()}
                >
                  {submitting ? "正在提交" : "提交审批申请"}
                </button>
              ) : null}
            </div>
          )}
        </>
      ) : (
        <div className="approval-info">
          <div>
            <strong>收银员当前只能查看</strong>
            <p>请把现场凭证交给老板或店长；本页不会向收银员开放预览、提交或直接处置。</p>
          </div>
        </div>
      )}
    </section>
  );
}
